import { CREATE_ACTIVITY_PRICING_STEP_KEYS } from '../createActivityAndPricingConsts';
import { LOT_OPTIONS } from '../ActivityItemPricing/ActivityItemPricingConsts';
import { PRICING_MODES } from '../ActivityItemPricing/PricingCalculator/PricingCalculatorConsts';
import isEqual from 'lodash/isEqual';
import { normalizeServerRule } from '../../utils/lotOptionsMapper';
import { groupRulesByProperties } from 'features/clients/contracts/MechanicalWorkConfig/utils/ruleGroupingUtils';
import { normalizePricing, normalizeRules } from '../../utils/lotOptionsMapper';

export const isStepModified = (originalSteps, currentSteps, stepIndex) => {
    if (!originalSteps) return false;

    switch (stepIndex) {
        case 0: {
            const origAct = originalSteps?.[CREATE_ACTIVITY_PRICING_STEP_KEYS.ACTIVITY_DETAILS];
            const currAct = currentSteps?.[CREATE_ACTIVITY_PRICING_STEP_KEYS.ACTIVITY_DETAILS];
            if (!origAct || !currAct) return false;
            const activityKeys = ['name', 'activityTypeId', 'noCharge', 'takePhotos', 'defaultStatus', 'pricingType', 'paramTypes'];
            if (activityKeys.some(key => !isEqual(origAct?.[key], currAct?.[key]))) return true;
            if (currAct?.materialsModified) return true;
            return false;
        }
        case 1:
            return !isEqual(
                normalizePricing(originalSteps[CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING]?.pricingItems),
                normalizePricing(currentSteps[CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING]?.pricingItems)
            );
        case 2:
            return !isEqual(
                normalizeRules(originalSteps[CREATE_ACTIVITY_PRICING_STEP_KEYS.RULES]?.rulesList),
                normalizeRules(currentSteps[CREATE_ACTIVITY_PRICING_STEP_KEYS.RULES]?.rulesList)
            );
        default:
            return false;
    }
};

export const hasAnyStepChanges = (originalSteps, currentSteps) => {
    if (!originalSteps) return false;
    return isStepModified(originalSteps, currentSteps, 0)
        || isStepModified(originalSteps, currentSteps, 1)
        || isStepModified(originalSteps, currentSteps, 2);
};

const sameVehicleTypeSet = (a, b) => {
    const keyOf = (ids) => [...(ids || [])].sort((x, y) => x - y).join(',');
    return keyOf(a) === keyOf(b);
};

// Preferred vendors are stored per (client, lot, vehicle types) — lot_id is NULL for the
// "all lots" / "all PMF lots" scopes — so resolve by matching the group's lot scope and
// exact vehicle-type set. The preferred_vendor table is shared with automation rules
// and the pricing matrix, so a looser match would surface values this pricing item
// never saved.
const resolvePreferredVendorId = (preferredVendors, clientId, lotOption, selectedLotIds, vehicleTypeIdsList) => {
    if (clientId == null) return null;
    const scopeMatches = preferredVendors
        .filter(pv =>
            pv.clientId === clientId &&
            (lotOption === LOT_OPTIONS.SPECIFIC_LOTS
                ? pv.lotId != null && selectedLotIds.includes(pv.lotId)
                : pv.lotId == null)
        )
        // Most recently written wins if stale sibling rows exist for the same scope
        .sort((x, y) => new Date(y.modifiedDate || y.createdDate || 0) - new Date(x.modifiedDate || x.createdDate || 0));
    const match = scopeMatches.find(pv => sameVehicleTypeSet(pv.vehicleTypeIdsList, vehicleTypeIdsList));
    return match?.vendorId ?? null;
};

export const mapServerPricingToStepFormat = (serverPricing = [], serverMaterialPrices = [], materials = [], serverPreferredVendors = []) => {
    const filtered = serverPricing;

    const materialPricesByPriceId = {};
    for (const mp of serverMaterialPrices) {
        // Handle both camelCase and PascalCase from server
        const priceId = mp.activityItemPriceId ?? mp.ActivityItemPriceId;
        if (!materialPricesByPriceId[priceId]) {
            materialPricesByPriceId[priceId] = [];
        }
        materialPricesByPriceId[priceId].push(mp);
    }

    const materialLookup = {};
    materials.forEach((m, index) => {
        if (m.id) materialLookup[m.id] = { index, materialName: m.materialName, unitOfMeasure: m.unitOfMeasure || '' };
    });

    // Group by client + price config, collect vendors and lots
    const groupsByClient = new Map();

    for (const item of filtered) {
        const clientKey = `${item.clientId ?? 'null'}`;

        if (!groupsByClient.has(clientKey)) {
            groupsByClient.set(clientKey, []);
        }
        groupsByClient.get(clientKey).push(item);
    }

    const result = [];

    // For each client, group by price configuration
    for (const [clientKey, clientItems] of groupsByClient) {
        const priceGroups = new Map();

        for (const item of clientItems) {
            let lotOption = LOT_OPTIONS.SPECIFIC_LOTS;
            if (item.allLots) lotOption = LOT_OPTIONS.ALL_LOTS;
            else if (item.allTenantLots) lotOption = LOT_OPTIONS.ALL_PMF_LOTS;

            const vehicleTypeIds = item.vehicleTypeIdsList ? [...item.vehicleTypeIdsList].sort().join(',') : '';
            const priceKey = `${lotOption}_${item.price ?? ''}_${item.priceMode ?? ''}_${item.charge ?? ''}_${item.surchargeAmount ?? ''}_${vehicleTypeIds}`;

            if (!priceGroups.has(priceKey)) {
                priceGroups.set(priceKey, {
                    items: [],
                    lotOption,
                    price: item.price,
                    priceMode: item.priceMode,
                    charge: item.charge,
                    surchargeAmount: item.surchargeAmount,
                    vehicleTypeIdsList: item.vehicleTypeIdsList || [],
                });
            }

            priceGroups.get(priceKey).items.push(item);
        }

        // Now for each price group, collect unique vendors and lots
        for (const [priceKey, priceGroup] of priceGroups) {
            const vendorSet = new Set();
            const lotSet = new Set();
            const originalIds = [];
            let firstItemWithMaterials = null;

            for (const item of priceGroup.items) {
                originalIds.push(item.id);
                if (item.vendorId) vendorSet.add(item.vendorId);
                if (item.lotId) lotSet.add(item.lotId);

                // Find first item that has material prices
                if (!firstItemWithMaterials && materialPricesByPriceId[item.id]?.length > 0) {
                    firstItemWithMaterials = item;
                }
            }

            // Get material prices from first item that has them
            const sourceItem = firstItemWithMaterials || priceGroup.items[0];
            const dbMaterialPrices = materialPricesByPriceId[sourceItem.id] || [];

            const mappedMaterialPrices = dbMaterialPrices
                .filter(mp => {
                    const materialId = mp.activityItemMaterialId ?? mp.ActivityItemMaterialId;
                    return materialLookup[materialId];
                })
                .map(mp => {
                    const materialId = mp.activityItemMaterialId ?? mp.ActivityItemMaterialId;
                    const cost = mp.cost ?? mp.Cost ?? 0;
                    const mat = materialLookup[materialId];
                    return {
                        materialIndex: mat.index,
                        materialName: mat.materialName,
                        unitOfMeasure: mat.unitOfMeasure,
                        cost: cost,
                        activityItemMaterialId: materialId,
                    };
                });

            const firstItem = priceGroup.items[0];
            const clientId = firstItem.clientId ?? null;
            const selectedLotIds = Array.from(lotSet);
            const vendorIds = Array.from(vendorSet);
            const resolvedPreferredVendorId = resolvePreferredVendorId(serverPreferredVendors, clientId, priceGroup.lotOption, selectedLotIds, priceGroup.vehicleTypeIdsList);

            result.push({
                id: firstItem.id,
                _originalIds: originalIds,
                isNew: false,
                isDefaultClientPricing: clientId == null,
                clientId,
                vendorIds,
                // A stale row can name a vendor that is no longer one of the group's vendors — treat that as unset.
                preferredVendorId: vendorIds.includes(resolvedPreferredVendorId) ? resolvedPreferredVendorId : null,
                lotOption: priceGroup.lotOption,
                selectedLotIds,
                vehicleTypeIdsList: priceGroup.vehicleTypeIdsList,
                priceConfig: {
                    priceMode: priceGroup.priceMode || PRICING_MODES.MARGIN.id,
                    price: priceGroup.price ?? null,
                    charge: priceGroup.charge ?? null,
                    surchargeAmount: priceGroup.surchargeAmount ?? null,
                    materialPrices: mappedMaterialPrices,
                },
            });
        }
    }

    return result;
};

export const mapServerRulesToStepFormat = (serverRules = []) => {
    const mappedRules = serverRules.map((rule) => {
        const normalized = normalizeServerRule(rule);
        
        return {
            ...normalized,
            isNew: false,
            clientId: normalized.clientId ?? null,
            lotId: Array.isArray(normalized.lotIds) ? normalized.lotIds : normalized.lotId ? (Array.isArray(normalized.lotId) ? normalized.lotId : [normalized.lotId]) : [],
            vehicleTypeIdsList: normalized.vehicleTypeIdsList || [],
            calcOnCheckin: normalized.calcOnCheckin || false,
            calcOnFirstCheckin: normalized.calcOnFirstCheckin || false,
            ruleParams: normalized.ruleParams || [],
            allLots: normalized.allLots || false,
            allTenantLots: normalized.allTenantLots || false,
        };
    });
    
    return groupRulesByProperties(mappedRules);
};
